import { useCallback, useEffect, useMemo, useState } from 'react';
import { limitToCents } from '../../lib/amount';
import { tokens } from '../forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import {
  DEPOSIT_LIMIT_REASON_MAX,
  PAUSE_REASON_MAX,
  activeLimitFor,
  buildLimitBody,
  capAmountError,
  isLimitUnchanged,
  limitProductOptions,
  pauseActionFor,
  pauseConfirmText,
} from '../../lib/depositLimit.helpers';
import commitmentsService from '../../services/commitments.service';
import managerService, { type ClientSummary } from '../../services/manager.service';
import type { Commitment, DepositLimit } from '../../types/commitments';

type Loaded<T> =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ok'; data: T };

/** Pause or resume one commitment, after a confirmation that says what it does. */
export function PauseControl({
  commitment,
  onDone,
}: Readonly<{ commitment: Commitment; onDone: (message: string) => void }>): JSX.Element {
  const action = pauseActionFor(commitment.status);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (action === null) {
    return (
      <span style={{ color: tokens.textMuted, fontSize: 12 }}>
        Pause and resume apply only to active or paused commitments.
      </span>
    );
  }
  const pause = action === 'pause';

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const note = reason.trim();
      await commitmentsService.toggleActivePaused(commitment.id, pause, note === '' ? null : note);
      setConfirming(false);
      setReason('');
      onDone(
        pause
          ? `Paused the ${commitment.product_name} commitment.`
          : `Resumed the ${commitment.product_name} commitment.`,
      );
    } catch (e) {
      setError(
        getApiErrorMessage(
          e,
          pause ? 'Could not pause this commitment.' : 'Could not resume this commitment.',
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} style={btnSecondary}>
        {pause ? 'Pause…' : 'Resume…'}
      </button>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 6, maxWidth: 420 }}>
      <p style={{ margin: 0, fontSize: 12 }}>{pauseConfirmText(commitment, pause)}</p>
      <label style={labelStyle}>
        Note for the audit trail (optional, up to {PAUSE_REASON_MAX} characters)
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={PAUSE_REASON_MAX}
          rows={2}
          style={inputStyle}
        />
      </label>
      {error ? <div style={errBoxStyle}>{error}</div> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={submit} disabled={busy} style={btnPrimary}>
          {pause ? 'Confirm pause' : 'Confirm resume'}
        </button>
        <button type="button" onClick={() => setConfirming(false)} disabled={busy} style={btnSecondary}>
          Keep as is
        </button>
      </div>
    </div>
  );
}

/** Set the monthly cap for one client and product, starting from the stored cap. */
export function DepositLimitForm({
  userId,
  products,
  limits,
  onSaved,
}: Readonly<{
  userId: string;
  products: { id: string; name: string }[];
  limits: DepositLimit[];
  onSaved: (message: string) => void;
}>): JSX.Element {
  const [productId, setProductId] = useState<string>(products[0]?.id ?? '');
  const active = activeLimitFor(limits, productId);
  const [cap, setCap] = useState<string>(limitToCents(active?.monthly_cap ?? ''));
  const [reason, setReason] = useState<string>(active?.reason ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start from the stored cap whenever the product (or the stored row) changes.
  useEffect(() => {
    setCap(limitToCents(active?.monthly_cap ?? ''));
    setReason(active?.reason ?? '');
    setError(null);
  }, [active?.id, active?.monthly_cap, active?.reason, productId]);

  if (products.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>
        This client has no commitment and no cap on any product, so there is nothing to set a cap on.
      </p>
    );
  }

  const unchanged = isLimitUnchanged(active, cap, reason);
  let blocked: string | null = null;
  if (cap.trim() === '') blocked = 'Enter the monthly cap to save.';
  else if (capAmountError(cap)) blocked = capAmountError(cap);
  else if (unchanged) blocked = 'Nothing changed: this is the cap already in force.';

  const submit = async (): Promise<void> => {
    if (capAmountError(cap) || unchanged) return;
    const body = buildLimitBody(cap, reason);
    setBusy(true);
    setError(null);
    try {
      await commitmentsService.setUserDepositLimit(userId, productId, body.monthly_cap, body.reason);
      onSaved('Monthly cap saved. The previous cap, if any, was closed today.');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not save the monthly cap.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 8, maxWidth: 480 }}>
      <label style={labelStyle}>
        <span>Product</span>
        <select value={productId} onChange={(e) => setProductId(e.target.value)} style={inputStyle}>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>
      <label style={labelStyle}>
        <span>Monthly cap (in the product&apos;s currency)</span>
        <input
          type="text"
          inputMode="decimal"
          value={cap}
          onChange={(e) => setCap(limitToCents(e.target.value))}
          placeholder="5000.00"
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        Reason (optional, up to {DEPOSIT_LIMIT_REASON_MAX} characters, audited)
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={DEPOSIT_LIMIT_REASON_MAX}
          rows={2}
          style={inputStyle}
        />
      </label>
      {blocked ? <p style={{ margin: 0, fontSize: 12, color: tokens.textMuted }}>{blocked}</p> : null}
      {error ? <div style={errBoxStyle}>{error}</div> : null}
      <div>
        <button type="button" onClick={submit} disabled={busy || blocked !== null} style={btnPrimary}>
          Save monthly cap
        </button>
      </div>
    </div>
  );
}

/** The caps recorded for a client, with an honest empty state. */
export function DepositLimitList({ limits }: Readonly<{ limits: DepositLimit[] }>): JSX.Element {
  if (limits.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>
        No monthly cap has ever been set for this client.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Product</th>
            <th style={thStyle}>Monthly cap</th>
            <th style={thStyle}>From</th>
            <th style={thStyle}>Until</th>
            <th style={thStyle}>Reason</th>
          </tr>
        </thead>
        <tbody>
          {limits.map((l) => (
            <tr key={l.id}>
              <td style={tdStyle}>{l.product_name}</td>
              <td style={tdStyle}>{l.monthly_cap}</td>
              <td style={tdStyle}>{l.effective_from}</td>
              <td style={tdStyle}>{l.effective_to ?? 'In force'}</td>
              <td style={tdStyle}>{l.reason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CommitmentRows({
  commitments,
  onDone,
}: Readonly<{ commitments: Commitment[]; onDone: (message: string) => void }>): JSX.Element {
  if (commitments.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>This client has no commitments.</p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Product</th>
            <th style={thStyle}>Cadence</th>
            <th style={thStyle}>Amount</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Pause / resume</th>
          </tr>
        </thead>
        <tbody>
          {commitments.map((c) => (
            <tr key={c.id}>
              <td style={tdStyle}>{c.product_name}</td>
              <td style={tdStyle}>{c.cadence}</td>
              <td style={tdStyle}>{formatMoney(c.amount, c.currency_code)}</td>
              <td style={tdStyle}>{c.status.replace('_', ' ')}</td>
              <td style={tdStyle}><PauseControl commitment={c} onDone={onDone} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One thing being loaded: a line while it loads, its error, or what it holds. */
function WhenLoaded<T>({
  value,
  children,
}: Readonly<{ value: Loaded<T>; children: (data: T) => JSX.Element }>): JSX.Element | null {
  if (value.state === 'loading') return <p>Loading…</p>;
  if (value.state === 'error') return <div style={errBoxStyle}>{value.message}</div>;
  if (value.state === 'ok') return children(value.data);
  return null;
}

/**
 * Manager tool: pick a client, see their commitments (pause / resume) and their
 * monthly deposit caps (set / replace). Manager and super_admin only; the page
 * that renders it is already behind that gate.
 */
export default function ClientCommitmentsPanel({
  onFlash,
}: Readonly<{ onFlash: (message: string) => void }>): JSX.Element {
  const [clients, setClients] = useState<Loaded<ClientSummary[]>>({ state: 'loading' });
  const [clientId, setClientId] = useState('');
  const [commitments, setCommitments] = useState<Loaded<Commitment[]>>({ state: 'idle' });
  const [limits, setLimits] = useState<Loaded<DepositLimit[]>>({ state: 'idle' });

  useEffect(() => {
    let cancelled = false;
    managerService
      .listClients()
      .then((data) => { if (!cancelled) setClients({ state: 'ok', data }); })
      .catch((e: unknown) => {
        if (!cancelled) {
          setClients({ state: 'error', message: getApiErrorMessage(e, 'Could not load clients.') });
        }
      });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async (id: string): Promise<void> => {
    setCommitments({ state: 'loading' });
    setLimits({ state: 'loading' });
    const [c, l] = await Promise.allSettled([
      commitmentsService.listForClient(id),
      commitmentsService.listUserDepositLimits(id),
    ]);
    setCommitments(
      c.status === 'fulfilled'
        ? { state: 'ok', data: c.value }
        : { state: 'error', message: getApiErrorMessage(c.reason, 'Could not load the commitments of this client.') },
    );
    setLimits(
      l.status === 'fulfilled'
        ? { state: 'ok', data: l.value }
        : { state: 'error', message: getApiErrorMessage(l.reason, 'Could not load the monthly caps of this client.') },
    );
  }, []);

  useEffect(() => {
    if (clientId) void load(clientId);
  }, [clientId, load]);

  const afterChange = (message: string): void => {
    onFlash(message);
    if (clientId) void load(clientId);
  };

  const products = useMemo(
    () =>
      limitProductOptions(
        commitments.state === 'ok' ? commitments.data : [],
        limits.state === 'ok' ? limits.data : [],
      ),
    [commitments, limits],
  );

  if (clients.state === 'error') return <div style={errBoxStyle}>{clients.message}</div>;
  if (clients.state === 'loading') return <p>Loading…</p>;
  const list = clients.state === 'ok' ? clients.data : [];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <label style={{ ...labelStyle, maxWidth: 480 }}>
        <span>Client</span>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={inputStyle}>
          <option value="">{list.length === 0 ? 'No clients to show' : '— choose a client —'}</option>
          {list.map((c) => (
            <option key={c.id} value={c.id}>{`${c.first_name} ${c.last_name} — ${c.email}`}</option>
          ))}
        </select>
      </label>

      {clientId === '' ? null : (
        <>
          <div>
            <h3 style={h3Style}>Commitments</h3>
            <WhenLoaded value={commitments}>
              {(data) => <CommitmentRows commitments={data} onDone={afterChange} />}
            </WhenLoaded>
          </div>
          <div>
            <h3 style={h3Style}>Monthly deposit caps</h3>
            <WhenLoaded value={limits}>
              {(data) => (
                <div style={{ display: 'grid', gap: 12 }}>
                  <DepositLimitList limits={data} />
                  <h4 style={{ ...h3Style, fontSize: 12 }}>Set or replace a cap</h4>
                  <DepositLimitForm
                    key={clientId}
                    userId={clientId}
                    products={products}
                    limits={data}
                    onSaved={afterChange}
                  />
                </div>
              )}
            </WhenLoaded>
          </div>
        </>
      )}
    </div>
  );
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  color: tokens.textPrimary,
  fontSize: 13,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: `1px solid ${tokens.border}`,
  color: tokens.textMuted,
};
const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: `1px solid ${tokens.border}`,
  verticalAlign: 'top',
};
const h3Style: React.CSSProperties = { margin: '0 0 8px 0', fontSize: 13, color: tokens.textPrimary };
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
};
const btnPrimary: React.CSSProperties = {
  background: tokens.successBorder,
  color: tokens.navy,
  border: 'none',
  borderRadius: 6,
  padding: '6px 12px',
  cursor: 'pointer',
  fontWeight: 600,
};
const btnSecondary: React.CSSProperties = {
  background: 'transparent',
  color: tokens.textPrimary,
  border: `1px solid ${tokens.border}`,
  borderRadius: 6,
  padding: '6px 12px',
  cursor: 'pointer',
  fontWeight: 600,
};
const errBoxStyle: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText,
  padding: 12,
  borderRadius: 8,
};
